import { useCallback, useEffect, useState } from 'react';
import { Box, CircularProgress, Grid, Typography } from '@mui/material';
import OpenInNewIcon from '@mui/icons-material/OpenInNew';
import CreditScoreOutlinedIcon from '@mui/icons-material/CreditScoreOutlined';

import api from '../../../services/api';
import { formatDateBR, formatMoney, isCPF } from '../../../utils';
import { useTranslation } from '../../../hooks/useTranslation';
import CreditAnalysisSection from '../../../components/CreditAnalysisSection';
import HistoryModal from '../../redCoins/HistoryModal';
import CustomTable from '../../../components/CustomTable';

const InfoField = ({ label, value, highlight, xs = 12, md = 6 }) => (
  <Grid item xs={xs} md={md}>
    <Typography component="div" sx={{ fontSize: '13px', lineHeight: 1.8 }}>
      <Box component="span" sx={{ color: '#6b7280' }}>
        {label}:{' '}
      </Box>
      <Box component="span" sx={{ fontWeight: 600, color: highlight ?? '#111827' }}>
        {value}
      </Box>
    </Typography>
  </Grid>
);

const DataCard = ({ title, children }) => (
  <Box sx={{ border: '1px solid #e5e7eb', borderRadius: '8px', overflow: 'hidden', mb: 2 }}>
    <Box sx={{ px: 2.5, py: 1.5, borderBottom: '1px solid #e5e7eb', backgroundColor: '#f9fafb' }}>
      <Typography
        sx={{ fontSize: '13px', fontWeight: 700, color: '#374151', textTransform: 'uppercase' }}
      >
        {title}
      </Typography>
    </Box>
    <Box sx={{ px: 2.5, py: 2, backgroundColor: '#fff' }}>{children}</Box>
  </Box>
);

const ConsultaCreditoTab = ({ simulation }) => {
  const { t } = useTranslation();

  const [orders, setOrders] = useState([]);
  const [loading, setLoading] = useState(false);
  const [modalOpen, setModalOpen] = useState(false);
  const [selectedRow, setSelectedRow] = useState(null);

  const valueOrDefault = (v) =>
    v === null || v === undefined || v === '' ? t('messages.noInformation') : v;

  const simulationId = simulation?.id;

  const fetchOrders = useCallback(async () => {
    if (!simulationId) return;
    try {
      setLoading(true);
      const customerId = simulation?.customer?.id;
      const { data } = await api.get(`/v1/simulation-super-adm/consult`, {
        params: { customer_id: customerId },
      });
      setOrders(data?.list ?? []);
    } catch {
      console.error(t('simulation.adm.detail.consult.errorFetching'));
    } finally {
      setLoading(false);
    }
  }, [simulationId]);

  useEffect(() => {
    fetchOrders();
  }, [fetchOrders]);

  const handleOpenModal = (row) => {
    setSelectedRow(row);
    setModalOpen(true);
  };

  const historyColumns = [
    {
      field: 'score',
      headerName: t('consultation.agroscore'),
      render: (value) => (
        <Typography sx={{ fontSize: '14px', fontWeight: 700 }}>{Math.floor(value ?? 0)}</Typography>
      ),
    },
    {
      field: 'company',
      headerName: t('consultation.consultResponsible'),
      render: (value) => value?.name ?? '-',
    },
    {
      field: 'created_at',
      headerName: t('consultation.consultDate'),
      render: (value) => formatDateBR(value),
    },
  ];

  const historyActions = [
    {
      icon: <OpenInNewIcon sx={{ fontSize: 18 }} />,
      tooltip: t('common.view'),
      onClick: handleOpenModal,
    },
  ];

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', py: 8 }}>
        <CircularProgress size={32} />
      </Box>
    );
  }

  if (orders.length === 0) {
    return (
      <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', py: 8, gap: 1.5 }}>
        <CreditScoreOutlinedIcon sx={{ fontSize: 56, color: '#d1d5db' }} />
        <Typography sx={{ fontSize: '15px', fontWeight: 600, color: '#374151' }}>
          {t('simulation.adm.detail.consult.noConsult')}
        </Typography>
        <Typography sx={{ fontSize: '13px', color: '#9e9e9e', textAlign: 'center', maxWidth: 380 }}>
          {t('simulation.adm.detail.consult.noConsultDescription')}
        </Typography>
      </Box>
    );
  }

  const lastOrder = orders[0];
  const c = lastOrder?.consult ?? {};
  const score = c.score ?? parseFloat(lastOrder?.score) ?? 0;
  const eventProb = c.event_probability ?? 0;

  return (
    <Box>
      <Box sx={{ mb: 3 }}>
        <Typography sx={{ fontSize: '18px', fontWeight: 700, color: '#111827' }}>
          {t('consultation.historyRedscore')}
        </Typography>
        <Typography sx={{ fontSize: '13px', color: '#6b7280', mt: 0.5 }}>
          {t('simulation.adm.detail.consult.lastConsult')}{' '}
          <strong>{formatDateBR(lastOrder?.created_at)}</strong>
        </Typography>
      </Box>

      {/* ── seção principal: gauge + dados ── */}
      <Grid container spacing={2} sx={{ mb: 2 }}>
        {/* gauge */}
        <Grid item xs={12} md={5}>
          <DataCard title={t('consultation.infoCredits')}>
            <CreditAnalysisSection
              score={score}
              riskPercentage={eventProb * 100}
              riskLabel={t('consultation.defaultProbability')}
            />
          </DataCard>
        </Grid>

        {/* dados financeiros + básicos */}
        <Grid item xs={12} md={7}>
          <DataCard title={t('consultation.financialData')}>
            <Grid container spacing={1}>
              <InfoField
                label={t('consultation.protests')}
                value={valueOrDefault(c.protest?.occurrences ?? 0)}
              />
              <InfoField
                label={t('consultation.judicialActions')}
                value={valueOrDefault(c.judicialAction?.occurrences ?? 0)}
              />
              <InfoField
                label={t('consultation.financialRestrictions')}
                value={valueOrDefault(c.financialRestrictions?.occurrences ?? 0)}
              />
              <InfoField
                label={t('consultation.bankruptcies')}
                value={
                  (c.bankruptcies?.occurrences ?? 0) > 0
                    ? t('consultation.yes')
                    : t('consultation.no')
                }
              />
              <InfoField
                label={t('consultation.financialPendencies')}
                value={
                  (c.financialPendencies?.occurrences ?? 0) > 0
                    ? formatMoney(c.financialPendencies.occurrences)
                    : 'R$ 0,00'
                }
              />
              <InfoField
                label={t('consultation.blockedByOwner')}
                value={
                  (c.blockedByDataOwner ?? false) ? t('consultation.yes') : t('consultation.no')
                }
                highlight={(c.blockedByDataOwner ?? false) ? '#c62828' : undefined}
              />
            </Grid>
          </DataCard>

          <DataCard title={t('consultation.basicInfo')}>
            <Grid container spacing={1}>
              <InfoField
                label={t('consultation.cadastralSituation')}
                value={valueOrDefault(c.tax_id_status)}
                highlight={
                  c.tax_id_status === 'Ativa' || c.tax_id_status === 'ATIVA' ? '#2e7d32' : undefined
                }
              />
              <InfoField label={t('consultation.taxRegime')} value={valueOrDefault(c.tax_regime)} />
              <InfoField
                label={t('consultation.riskClassification')}
                value={valueOrDefault(c.risk_screening)}
                highlight={
                  (c.risk_screening || '').toLowerCase() === 'baixo'
                    ? '#2e7d32'
                    : (c.risk_screening || '').toLowerCase() === 'alto'
                      ? '#c62828'
                      : undefined
                }
              />
              <InfoField
                label={t('consultation.defaultRisk')}
                value={`${(eventProb * 100).toFixed(2)} %`}
                highlight={eventProb * 100 > 5 ? '#c62828' : '#2e7d32'}
              />
              <>
                <InfoField
                  label={t('consultation.tradeName')}
                  value={valueOrDefault(c.tradeName ?? c.trade_name)}
                />
                <InfoField
                  label={t('consultation.openingDate')}
                  value={valueOrDefault(c.openingDate ?? c.opening_date)}
                />
                <InfoField
                  label={t('consultation.companySize')}
                  value={valueOrDefault(c.companySize ?? c.company_size)}
                />
                <InfoField
                  label={t('consultation.socialCapital')}
                  value={c.capital ? formatMoney(c.capital) : '-'}
                />
                <InfoField
                  label={t('consultation.operabilityIndicator')}
                  value={valueOrDefault(c.operabilityIndicator ?? c.operability_indicator)}
                  highlight={
                    ((c.operabilityIndicator ?? c.operability_indicator) || '').toLowerCase() ===
                    'operável'
                      ? '#2e7d32'
                      : undefined
                  }
                />
              </>
            </Grid>
          </DataCard>
        </Grid>
      </Grid>

      {/* ── histórico de consultas ── */}
      <DataCard title={t('simulation.adm.detail.consult.history')}>
        <CustomTable
          columns={historyColumns}
          rows={orders.slice(1)}
          actions={historyActions}
          emptyMessage={t('simulation.adm.detail.consult.noConsult')}
          elevation={0}
          rowsPerPageOptions={[5, 10]}
        />
      </DataCard>

      {/* ── modal de detalhe ── */}
      <HistoryModal
        open={modalOpen}
        handleClose={() => {
          setModalOpen(false);
          setSelectedRow(null);
        }}
        row={selectedRow}
        loading={false}
      />
    </Box>
  );
};

export default ConsultaCreditoTab;
